import React, { Fragment, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import moment from "moment";
import {
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Grid,
  ListItemText,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { DateRangePicker } from "@material-ui/pickers";
import * as C from "components";
import {
  setCurrentPage,
  getPicTreeRegularSeasonEventBootstrapData,
  getPicTreeRegularSeasonEventAreaData,
  setPicTreeRegularSeasonEventPageData,
} from "actions";
import ByAreaSummary from "components/PicTree/Game/Area/ByAreaSummary";
import ByAreaDetailTable from "components/PicTree/Game/Area/ByAreaDetailTable";
import SingleSelect from "components/Select/SingleSelect";
import { EVENT_AREA_LAYER_OPTIONS } from "./PicTreeRegularSeasonEventByAreaFixtures";

const ALL_BOOST_EVENTS_VALUE = "__ALL__";

export const getEventCompositeKey = (event) => (
  `${event?.gameSpaceId || event?.game_space_id || ""}:${event?.boostEventId || event?.boost_event_id || ""}`
);

export const getSelectedEventOptions = (eventOptions, selectedEventKeys) => {
  const selectedKeys = new Set((Array.isArray(selectedEventKeys) ? selectedEventKeys : []).map(getEventCompositeKey));
  return (Array.isArray(eventOptions) ? eventOptions : []).filter((option) => selectedKeys.has(getEventCompositeKey(option)));
};

const useStyles = makeStyles((theme) => ({
  breadcrumbs: { marginTop: 16, minHeight: 30 },
  topBar: { marginTop: -16, marginBottom: 8, flexWrap: "nowrap" },
  headerTitle: { flex: "0 0 auto" },
  filters: { display: "flex", justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap", gap: theme.spacing(1), minWidth: 0, overflowX: "hidden" },
  eventSelect: {
    flex: "0 1 560px",
    minWidth: 0,
    maxWidth: "100%",
    "& .MuiAutocomplete-inputRoot": { flexWrap: "wrap" },
    "& .MuiAutocomplete-tag": { maxWidth: "100%", whiteSpace: "nowrap" },
    "& .MuiChip-label": { whiteSpace: "nowrap" },
  },
  dateRange: { display: "flex", alignItems: "center", width: 276, flexShrink: 0 },
  dateInput: { display: "flex", alignItems: "baseline" },
  dateField: { width: 120 },
  delimiter: { margin: "0 4px", color: "#556070" },
  goButton: { height: 32, minWidth: 60, color: "#fff", flexShrink: 0 },
  layerLabel: { color: "#556070", whiteSpace: "nowrap" },
}));

export const PicTreeRegularSeasonEventByArea = ({
  setCurrentPage,
  getPicTreeRegularSeasonEventBootstrapData,
  selectedSeasonId = null,
  selectedSeasonIds = [],
  selectedBoostEventId = ALL_BOOST_EVENTS_VALUE,
  selectedEventKeys = [],
  draftSelectedEventKeys = [],
  reduxDraftDateRange = [null, null],
  selectedDateRange = [null, null],
  eventRows = [],
  eventRowsLoading = false,
  eventRowsError = null,
  bootstrapEventReady = false,
  getPicTreeRegularSeasonEventAreaData,
  setPicTreeRegularSeasonEventPageData,
  areaSummary = null,
  areaTable = [],
  areaLoading = false,
  areaError = null,
}) => {
  const classes = useStyles();
  const [draftRange, setDraftRange] = useState(reduxDraftDateRange);
  const [layerId, setLayerId] = useState("regular_1_l1");
  const [initialAreaDataCall, setInitialAreaDataCall] = useState(true);

  useEffect(() => {
    setCurrentPage(window.location.pathname);
    getPicTreeRegularSeasonEventBootstrapData({});
  }, [getPicTreeRegularSeasonEventBootstrapData, setCurrentPage]);

  useEffect(() => {
    if (reduxDraftDateRange?.[0] && reduxDraftDateRange?.[1]) {
      setDraftRange([
        moment(reduxDraftDateRange[0], "YYYY-MM-DD"),
        moment(reduxDraftDateRange[1], "YYYY-MM-DD"),
      ]);
    }
  }, [reduxDraftDateRange]);

  useEffect(() => {
    if (!initialAreaDataCall || !bootstrapEventReady || selectedEventKeys.length !== 1
      || !selectedDateRange?.[0] || !selectedDateRange?.[1]) return;
    getPicTreeRegularSeasonEventAreaData({
      selectedSeasonId,
      selectedSeasonIds,
      selectedBoostEventId,
      selectedEventKeys,
      selectedAreaLayerId: layerId,
      selectedDateRange,
    });
    setInitialAreaDataCall(false);
  }, [
    bootstrapEventReady,
    getPicTreeRegularSeasonEventAreaData,
    initialAreaDataCall,
    layerId,
    selectedBoostEventId,
    selectedDateRange,
    selectedEventKeys,
    selectedSeasonId,
    selectedSeasonIds,
  ]);

  const options = useMemo(() => (eventRows || []).filter((row, index, rows) => (
    row?.id && row?.seasonId && rows.findIndex((candidate) => (
      candidate.id === row.id && candidate.seasonId === row.seasonId
    )) === index
  )), [eventRows]);

  const eventOptions = useMemo(() => options.map((row) => ({
    key: `${row.seasonId}:${row.id}`,
    gameSpaceId: row.seasonId,
    boostEventId: row.id,
    label: row.name || row.id,
    seasonName: row.seasonName || row.seasonId,
    seasonAbbreviation: row.seasonAbbreviation || "",
    period: row.period || "",
    startDatetime: row.startDatetime,
    endDatetime: row.endDatetime,
  })), [options]);

  const draftEventKeys = Array.isArray(draftSelectedEventKeys) ? draftSelectedEventKeys : selectedEventKeys;
  const selectedEventOptions = getSelectedEventOptions(eventOptions, draftEventKeys);

  const deriveEventDateRange = (selectedOptions) => {
    const validOptions = (selectedOptions || []).filter((option) => option.startDatetime && option.endDatetime);
    if (!validOptions.length) return [null, null];
    const earliestStart = validOptions.reduce((earliest, option) => (
      moment.utc(option.startDatetime).isBefore(earliest) ? moment.utc(option.startDatetime) : earliest
    ), moment.utc(validOptions[0].startDatetime));
    const latestEnd = validOptions.reduce((latest, option) => (
      moment.utc(option.endDatetime).isAfter(latest) ? moment.utc(option.endDatetime) : latest
    ), moment.utc(validOptions[0].endDatetime));
    const today = moment().tz("Asia/Tokyo").startOf("day");
    const startJst = earliestStart.tz("Asia/Tokyo").startOf("day");
    const cappedEnd = moment.min(latestEnd.tz("Asia/Tokyo").startOf("day"), today);
    if (cappedEnd.isBefore(startJst)) {
      return [today.format("YYYY-MM-DD"), today.format("YYYY-MM-DD")];
    }
    return [startJst.format("YYYY-MM-DD"), cappedEnd.format("YYYY-MM-DD")];
  };

  const handleBoostEventChange = (_event, nextOptions) => {
    const nextKeys = nextOptions.map((option) => ({
      gameSpaceId: option.gameSpaceId,
      boostEventId: option.boostEventId,
    }));
    const nextDateRange = deriveEventDateRange(nextOptions);
    setDraftRange([
      nextDateRange[0] ? moment(nextDateRange[0], "YYYY-MM-DD") : null,
      nextDateRange[1] ? moment(nextDateRange[1], "YYYY-MM-DD") : null,
    ]);
    setPicTreeRegularSeasonEventPageData({
      draftSelectedEventKeys: nextKeys,
      draftDateRange: nextDateRange,
    });
  };

  const handleDateRangeChange = (nextValue) => {
    const nextRange = nextValue || [null, null];
    setDraftRange(nextRange);
    setPicTreeRegularSeasonEventPageData({
      draftDateRange: nextRange.map((date) => (date ? moment(date).format("YYYY-MM-DD") : null)),
    });
  };

  const handleLayerChange = (event) => {
    const nextLayerId = event.target.value;
    setLayerId(nextLayerId);
    if (selectedEventKeys.length !== 1 || !selectedDateRange?.[0] || !selectedDateRange?.[1]) return;
    getPicTreeRegularSeasonEventAreaData({
      selectedSeasonId,
      selectedSeasonIds,
      selectedBoostEventId,
      selectedEventKeys,
      selectedAreaLayerId: nextLayerId,
      selectedDateRange,
    });
  };

  const handleGo = () => {
    const nextDateRange = draftRange?.[0] && draftRange?.[1]
      ? [moment(draftRange[0]).format("YYYY-MM-DD"), moment(draftRange[1]).format("YYYY-MM-DD")]
      : [null, null];
    const appliedBoostEventId = draftEventKeys.length === 1
      ? draftEventKeys[0].boostEventId
      : ALL_BOOST_EVENTS_VALUE;
    setPicTreeRegularSeasonEventPageData({
      selectedEventKeys: draftEventKeys,
      selectedBoostEventId: appliedBoostEventId,
      selectedDateRange: nextDateRange,
      draftSelectedEventKeys: draftEventKeys,
      draftDateRange: nextDateRange,
    });
    if (!draftEventKeys.length) {
      getPicTreeRegularSeasonEventAreaData({
        selectedSeasonId,
        selectedSeasonIds,
        selectedBoostEventId: ALL_BOOST_EVENTS_VALUE,
        selectedEventKeys: [],
        selectedAreaLayerId: layerId,
        selectedDateRange: nextDateRange,
      });
      return;
    }
    const nextRange = [
      nextDateRange[0],
      nextDateRange[1],
    ];
    getPicTreeRegularSeasonEventAreaData({
      selectedSeasonId,
      selectedSeasonIds,
      selectedBoostEventId: appliedBoostEventId,
      selectedEventKeys: draftEventKeys,
      selectedAreaLayerId: layerId,
      selectedDateRange: nextRange,
    });
  };

  return (
    <Fragment>
      <C.ContentWrapper>
        <Grid container spacing={1} className={classes.topBar} justifyContent="space-between" alignItems="center">
          <Grid item className={classes.headerTitle}>
            <Breadcrumbs separator="›" className={classes.breadcrumbs}>
              <Typography>PicTrée 全体</Typography>
              <Typography>レギュラーシーズン</Typography>
              <Typography>イベント</Typography>
              <Typography>エリア別</Typography>
            </Breadcrumbs>
          </Grid>
          <Grid item xs className={classes.filters}>
            <Autocomplete
              multiple
              size="small"
              className={classes.eventSelect}
              disableCloseOnSelect
              options={eventOptions}
              value={selectedEventOptions}
              loading={eventRowsLoading}
              disabled={eventRowsLoading}
              limitTags={2}
              getOptionLabel={(option) => option.label || option.boostEventId}
              getOptionSelected={(option, value) => getEventCompositeKey(option) === getEventCompositeKey(value)}
              noOptionsText={eventRowsError ? "イベント一覧の取得に失敗しました" : "選択可能なイベントがありません"}
              loadingText="イベントを読み込み中"
              onChange={handleBoostEventChange}
              renderOption={(option, { selected }) => (
                <Fragment>
                  <Checkbox
                    checked={selected || selectedEventOptions.some((selectedOption) => (
                      getEventCompositeKey(selectedOption) === getEventCompositeKey(option)
                    ))}
                    color="primary"
                    style={{ marginRight: 8 }}
                  />
                  <ListItemText
                    primary={option.label}
                    secondary={`${option.seasonName || option.seasonAbbreviation || ""}${option.period ? ` / ${option.period}` : ""}`}
                  />
                </Fragment>
              )}
              renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => (
                <Chip
                  label={`${option.label}${option.seasonAbbreviation ? ` (${option.seasonAbbreviation})` : ""}`}
                  size="small"
                  {...getTagProps({ index })}
                  key={option.key}
                />
              ))}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" inputProps={{ ...params.inputProps, "aria-label": "イベント" }} />
              )}
            />
            <span className={classes.layerLabel}>階層：</span>
            <SingleSelect
              selected={{ value: layerId }}
              options={EVENT_AREA_LAYER_OPTIONS}
                handleChange={handleLayerChange}
            />
            <div className={classes.dateRange}>
              <DateRangePicker
                startText="Start"
                endText="End"
                disableHighlightToday
                showToolbar={false}
                allowSameDateSelection
                maxDate={moment().format("YYYY-MM-DD")}
                value={draftRange}
                  onChange={handleDateRangeChange}
                renderInput={(startProps, endProps) => (
                  <div className={classes.dateInput}>
                    <TextField {...startProps} error={false} size="small" className={classes.dateField} helperText="" />
                    <span className={classes.delimiter}>~</span>
                    <TextField {...endProps} error={false} size="small" className={classes.dateField} helperText="" />
                  </div>
                )}
              />
            </div>
            <Button variant="contained" color="primary" className={classes.goButton} disableElevation onClick={handleGo}>GO</Button>
          </Grid>
        </Grid>

        <Card>
          <CardContent>
            {areaLoading && <Typography color="textSecondary">データ取得中です。</Typography>}
            {areaError && <Typography color="error">{areaError}</Typography>}
            <ByAreaSummary data={areaSummary} />
          </CardContent>
        </Card>
        <Grid container spacing={1} style={{ marginTop: 16 }}>
          <Grid item xs={12}>
            <ByAreaDetailTable
              data={areaTable}
              selectedAreaLayerId={layerId}
              rowsPerPage={200}
            />
          </Grid>
        </Grid>
        {/* <Typography variant="caption" color="textSecondary">
          適用期間: {draftRange?.[0] ? moment(draftRange[0]).format("YYYY-MM-DD") : "-"} ~ {draftRange?.[1] ? moment(draftRange[1]).format("YYYY-MM-DD") : "-"}、対象イベント: {effectiveEventId || "未選択"}
        </Typography> */}
      </C.ContentWrapper>
    </Fragment>
  );
};

PicTreeRegularSeasonEventByArea.propTypes = {
  setCurrentPage: PropTypes.func,
  getPicTreeRegularSeasonEventBootstrapData: PropTypes.func,
  selectedSeasonId: PropTypes.string,
  selectedSeasonIds: PropTypes.array,
  seasonOptions: PropTypes.array,
  companyOptions: PropTypes.array,
  selectedCompanyCode: PropTypes.string,
  selectedBoostEventId: PropTypes.string,
  selectedEventKeys: PropTypes.array,
  draftSelectedEventKeys: PropTypes.array,
  reduxDraftDateRange: PropTypes.array,
  selectedDateRange: PropTypes.array,
  eventRows: PropTypes.array,
  eventRowsLoading: PropTypes.bool,
  eventRowsError: PropTypes.object,
  bootstrapEventReady: PropTypes.bool,
  getPicTreeRegularSeasonEventAreaData: PropTypes.func,
  areaSummary: PropTypes.object,
  areaTable: PropTypes.array,
  areaLoading: PropTypes.bool,
  areaError: PropTypes.string,
};

const mapStateToProps = (state) => ({
  selectedSeasonId: state.pictreeRegularSeasonEvent.selectedSeasonId,
  selectedSeasonIds: state.pictreeRegularSeasonEvent.selectedSeasonIds,
  selectedBoostEventId: state.pictreeRegularSeasonEvent.selectedBoostEventId,
  selectedEventKeys: state.pictreeRegularSeasonEvent.selectedEventKeys,
  draftSelectedEventKeys: state.pictreeRegularSeasonEvent.draftSelectedEventKeys,
  reduxDraftDateRange: state.pictreeRegularSeasonEvent.draftDateRange,
  selectedDateRange: state.pictreeRegularSeasonEvent.selectedDateRange,
  seasonOptions: state.pictreeRegularSeasonEvent.seasonOptions,
  companyOptions: state.pictreeRegularSeasonEvent.companyOptions,
  selectedCompanyCode: state.pictreeRegularSeasonEvent.selectedCompanyCode,
  eventRows: state.pictreeRegularSeasonEvent.eventRows,
  eventRowsLoading: state.pictreeRegularSeasonEvent.eventRowsLoading,
  eventRowsError: state.pictreeRegularSeasonEvent.eventRowsError,
  bootstrapEventReady: state.pictreeRegularSeasonEvent.bootstrapEventReady,
  areaSummary: state.pictreeRegularSeasonEvent.areaSummary,
  areaTable: state.pictreeRegularSeasonEvent.areaTable,
  areaLoading: state.pictreeRegularSeasonEvent.areaLoading,
  areaError: state.pictreeRegularSeasonEvent.areaError,
});

export const mapDispatchToProps = (dispatch) => ({
  setCurrentPage: (param) => dispatch(setCurrentPage(param)),
  getPicTreeRegularSeasonEventBootstrapData: (param) => dispatch(getPicTreeRegularSeasonEventBootstrapData(param)),
  getPicTreeRegularSeasonEventAreaData: (param) => dispatch(getPicTreeRegularSeasonEventAreaData(param)),
  setPicTreeRegularSeasonEventPageData: (param) => dispatch(setPicTreeRegularSeasonEventPageData(param)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PicTreeRegularSeasonEventByArea);
